import * as React from "react";
import {
  createTableColumn,
  TableCellLayout,
  TableColumnDefinition,
  Theme,
} from "@fluentui/react-components";
import { PaymentTransactionLog } from "../../KulcsUzletSvc";

const getCibValue = (
  item: PaymentTransactionLog,
  key: string,
  fallbackKey?: keyof PaymentTransactionLog
): string => {
  let value: any = "";

  try {
    const parsed = item.cibMessageStr ? JSON.parse(item.cibMessageStr) : null;
    value = parsed?.[key];

    if (value === undefined || value === null) {
      if (fallbackKey) {
        value = item[fallbackKey];
      } else {
        value = "";
      }
    }
  } catch (e) {
    value = fallbackKey ? item[fallbackKey] : "";
  }

  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

// ---------------------------------------------------------------------
// Styles / Állapotkezelés (Változatlanul hagyva)
// ---------------------------------------------------------------------

export const columnSizingOptionsTransaction = {
  customerCode: {
    minWidth: 100,
    defaultWidth: 150,
  },
  paymentUserEmail: {
    minWidth: 100,
    defaultWidth: 200,
  },
  state: {
    minWidth: 100,
    defaultWidth: 150,
  },
  transactionId: {
    minWidth: 100,
    defaultWidth: 150,
  },
  transactionState: {
    minWidth: 100,
    defaultWidth: 150,
  },
  rowVersion: {
    minWidth: 100,
    defaultWidth: 150,
  },
  expiredDate: {
    minWidth: 100,
    defaultWidth: 150,
  },
  amount: {
    minWidth: 100,
    defaultWidth: 150,
  },
  discount: {
    minWidth: 100,
    defaultWidth: 150,
  },
  responseCode: {
    minWidth: 100,
    defaultWidth: 150,
  },
};

const getStateStyle = (value?: string): React.CSSProperties => {
  switch (value) {
    case "ACTIVE":
    case "INVOICED":
    case "FINISHED":
    case "FREE":
    case "INVOICE_CREATED":
      return {
        backgroundColor: "#22c55e",
        color: "white",
        padding: "2px 6px",
        borderRadius: "4px",
      };
    case "TRIAL":
    case "STARTED":
    case "PENDING":
    case "INACTIVE":
    case "INVOICE_CREATING":
    case "INVOICE_PROCESSING":
    case "UNDER_PAYMENT":
      return {
        backgroundColor: "#facc15",
        color: "black",
        padding: "2px 6px",
        borderRadius: "4px",
      };
    case "FORCECLOSED":
      return {
        backgroundColor: "#f97316",
        color: "white",
        padding: "2px 6px",
        borderRadius: "4px",
      };
    case "FAILED":
    case "RENEWAL_ERROR":
      return {
        backgroundColor: "#ef4444",
        color: "white",
        padding: "2px 6px",
        borderRadius: "4px",
      };
    case "UNSUBSRIBE":
      return {
        backgroundColor: "#ecef44ff",
        padding: "2px 6px",
        borderRadius: "4px",
      };
    default:
      return {
        backgroundColor: "#e5e7eb",
        color: "black",
        padding: "2px 6px",
        borderRadius: "4px",
      };
  }
};

export const getColumnsTransaction = (theme: Theme) =>
  [
    createTableColumn<PaymentTransactionLog>({
      columnId: "msgt",
      renderHeaderCell: () => "MSGT",
      compare: (a, b) => {
        const aValue = getCibValue(a, "MSGT", "messageType");
        const bValue = getCibValue(b, "MSGT", "messageType");
        return aValue.localeCompare(bValue);
      },
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "MSGT", "messageType");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "pid",
      renderHeaderCell: () => "PID",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) => {
        const aValue = getCibValue(a, "PID");
        const bValue = getCibValue(b, "PID");
        return aValue.localeCompare(bValue);
      },
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "PID");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "trid",
      renderHeaderCell: () => "TRID",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) =>
        (a.transactionId ?? "").localeCompare(b.transactionId ?? ""),
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        return (
          <TableCellLayout truncate>{item.transactionId ?? ""}</TableCellLayout>
        );
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "amo",
      renderHeaderCell: () => "AMO",
      // --- RENDEZÉS HOZZÁADVA (Számként hasonlítjuk, ha lehetséges) ---
      compare: (a, b) => {
        const aValueStr = getCibValue(a, "AMO");
        const bValueStr = getCibValue(b, "AMO");
        const aValueNum = parseFloat(aValueStr) || 0;
        const bValueNum = parseFloat(bValueStr) || 0;
        return aValueNum - bValueNum;
      },
      // ----------------------------------------------------------------
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "AMO");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "rc",
      renderHeaderCell: () => "RC",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) => {
        const aValue = getCibValue(a, "RC");
        const bValue = getCibValue(b, "RC");
        return aValue.localeCompare(bValue);
      },
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "RC");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "rt",
      renderHeaderCell: () => "RT",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) => {
        const aValue = getCibValue(a, "RT");
        const bValue = getCibValue(b, "RT");
        return aValue.localeCompare(bValue);
      },
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "RT");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "anum",
      renderHeaderCell: () => "ANUM",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) => {
        const aValue = getCibValue(a, "ANUM");
        const bValue = getCibValue(b, "ANUM");
        return aValue.localeCompare(bValue);
      },
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "ANUM");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "cnum",
      renderHeaderCell: () => "CNUM",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) => {
        const aValue = getCibValue(a, "CNUM");
        const bValue = getCibValue(b, "CNUM");
        return aValue.localeCompare(bValue);
      },
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "CNUM");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "recid",
      renderHeaderCell: () => "RECID",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) => {
        const aValue = getCibValue(a, "RECID");
        const bValue = getCibValue(b, "RECID");
        return aValue.localeCompare(bValue);
      },
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        const value = getCibValue(item, "RECID");
        return <TableCellLayout truncate>{value}</TableCellLayout>;
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,

    createTableColumn<PaymentTransactionLog>({
      columnId: "rowVersion",
      renderHeaderCell: () => "RowVersion",
      // --- RENDEZÉS HOZZÁADVA ---
      compare: (a, b) => (a.rowVersion ?? "").localeCompare(b.rowVersion ?? ""),
      // --------------------------
      renderCell: (item: PaymentTransactionLog) => {
        return (
          <TableCellLayout truncate>{item.rowVersion ?? ""}</TableCellLayout>
        );
      },
    }) as TableColumnDefinition<PaymentTransactionLog>,
  ] as TableColumnDefinition<PaymentTransactionLog>[];
